import { useTranslation } from "react-i18next";
import {
  getByFieldValue,
  getTimezoneFromUser,
  isBlank,
  isEmptyList,
  isNullOrUndefined,
  isSearchQueryMatch,
  sortByDateField,
  sortByField,
} from "../../utils";
import "./styles.css";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { useMainContext, useMainDispatchContext } from "../../MainContext";
import { getAirportDetailByUuid, getTurnaroundsSummarySearch } from "../../api";

import { POLLING_INTERVALS, SEARCH_QUERY_PLACEHOLDERS } from "../../constants";
import LoadingIndicator from "../LoadingIndicator";
import { getTurnaroundInfoForTurnaroundSummary } from "../../turnaroundUtils";
import TimeChart from "../TimeChart";
import { getDuration } from "../../dateUtils";
import {
  TIME_CHART_ICONS,
  TIME_CHART_SIZE,
  TIME_CHART_VARIANTS,
} from "../TimeChart/timeChartUtils";
import TurnaroundEditModal from "../TurnaroundsModule/TurnaroundEditModal";
import {
  ResourceActions,
  userHasAnyResourceAction,
} from "../../resourceActionsUtils";
import StandDetailModal from "./StandDetailModal";

function StandControl() {
  const { t } = useTranslation();

  const dispatch = useMainDispatchContext();
  const mainContext = useMainContext();
  const { currentUser, turnaroundsSummarySearch, searchQuery, airportDetail } =
    mainContext;
  const airportTimezone = getTimezoneFromUser(currentUser);
  const mNow = moment().tz(airportTimezone);

  const [refreshStandsRequested, setRefreshStandsRequested] = useState(false);
  const [timelineRanges, setTimelineRanges] = useState({
    mRangeStart: moment(mNow).startOf("hour").add(-2, "hour"),
    mRangeEnd: moment(mNow).startOf("hour").add(10, "hour"),
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedTurnaroundUuidForEdit, setSelectedTurnaroundUuidForEdit] =
    useState(null);
  const [selectedTimeChartInfo, setSelectedTimeChartInfo] = useState(null);

  const hasEditTurnProfile = userHasAnyResourceAction(
    currentUser,
    ResourceActions.EditTurnProfile
  );

  useEffect(() => {
    // Interval for refreshing the data within the timeframe
    const intervals = [];

    if (POLLING_INTERVALS.LIVE_STANDS > 0) {
      const interval = setInterval(() => {
        setRefreshStandsRequested(true);
      }, POLLING_INTERVALS.LIVE_STANDS);
      intervals.push(interval);
    }
    return () => {
      for (let i = 0; i < intervals.length; i++) {
        clearInterval(intervals[i]);
      }
    };
  }, []);

  useEffect(() => {
    if (!setRefreshStandsRequested) return () => {};
    const mNowUpdated = moment().tz(airportTimezone);
    setTimelineRanges({
      mRangeStart: moment(mNowUpdated).startOf("hour").add(-2, "hour"),
      mRangeEnd: moment(mNowUpdated).startOf("hour").add(10, "hour"),
    });
    setRefreshStandsRequested(false);
  }, [refreshStandsRequested, airportTimezone]);

  // Fetch supporting data
  useEffect(() => {
    if (!isNullOrUndefined(currentUser)) {
      getAirportDetailByUuid(dispatch, { uuid: currentUser?.airport.uuid });
    }
  }, [dispatch, currentUser]);

  // Fetch the turnaround summaries
  useEffect(() => {
    if (isNullOrUndefined(timelineRanges)) return () => {};
    getTurnaroundsSummarySearch(dispatch, {
      startTime: timelineRanges.mRangeStart
        .tz(airportTimezone)
        .toDate()
        .toISOString(),
      endTime: timelineRanges.mRangeEnd
        .tz(airportTimezone)
        .toDate()
        .toISOString(),
      includeOpen: true,
      includeClosed: true,
    });
  }, [dispatch, timelineRanges, airportTimezone]);

  function handleReloadTurnarounds() {
    getTurnaroundsSummarySearch(dispatch, {
      startTime: timelineRanges.mRangeStart
        .tz(airportTimezone)
        .toDate()
        .toISOString(),
      endTime: timelineRanges.mRangeEnd
        .tz(airportTimezone)
        .toDate()
        .toISOString(),
      includeOpen: true,
      includeClosed: true,
    });
  }

  // Main function to calculate the time chart items
  const [timeChartItems] = useMemo(() => {
    const timeChartItemsToRender = [];
    const timeChartItemsToRenderNotAssigned = [];
    if (
      !isEmptyList(turnaroundsSummarySearch?.turnarounds) &&
      !isNullOrUndefined(airportDetail) &&
      !isNullOrUndefined(currentUser)
    ) {
      // Process each turnaround and bucket by stand
      const turnaroundInfoList = turnaroundsSummarySearch.turnarounds.map((i) =>
        getTurnaroundInfoForTurnaroundSummary(i)
      );

      for (let i = 0; i < turnaroundInfoList.length; i++) {
        const turnaroundInfo = turnaroundInfoList[i];
        const effectiveStandName = !isNullOrUndefined(
          turnaroundInfo.snappedStandName
        )
          ? turnaroundInfo.snappedStandName
          : turnaroundInfo.assignedStandName;
        const effectiveStand = getByFieldValue(
          airportDetail.stands,
          "name",
          effectiveStandName
        );

        const matchTurnaroundName =
          !isBlank(turnaroundInfo.combinedFlightName) &&
          isSearchQueryMatch(
            searchQuery.searchTerm,
            turnaroundInfo.combinedFlightName
          );
        const matchStandName =
          !isBlank(effectiveStandName) &&
          isSearchQueryMatch(searchQuery.searchTerm, effectiveStandName);
        if (
          isBlank(searchQuery?.searchTerm) ||
          matchTurnaroundName ||
          matchStandName
        ) {
          const startTime = turnaroundInfo.resolvedInboundTimes.displayTime;
          const endTime = turnaroundInfo.resolvedOutboundTimes.displayTime;
          const actualInbound = turnaroundInfo.resolvedInboundTimes.actualTime;
          const actualOutbound =
            turnaroundInfo.resolvedOutboundTimes.actualTime;
          const duration = !isNullOrUndefined(endTime)
            ? getDuration(startTime, endTime)
            : 0;

          const existingChartItem = !isNullOrUndefined(effectiveStand)
            ? getByFieldValue(
                timeChartItemsToRender,
                "uuid",
                effectiveStand.uuid
              )
            : null;

          const title = !isNullOrUndefined(effectiveStand)
            ? `${t("stand_non_cap")}: ${effectiveStand.name}`
            : `${t("stand_non_cap")}: ${t("not_assigned")}`;

          const hasDeparted = !isNullOrUndefined(actualOutbound);
          const hasArrived = !hasDeparted && !isNullOrUndefined(actualInbound);
          const timeChartBlockInfo = {
            uuid: turnaroundInfo.uuid,
            barInfo: turnaroundInfo,
            title: title,
            subtitle: turnaroundInfo.combinedFlightName,
            details: turnaroundInfo.remarks,
            startLabel: turnaroundInfo.inboundFlightName,
            endLabel: turnaroundInfo.outboundFlightName,
            startTime: startTime,
            endTime: endTime,
            duration: duration,
            variant: hasDeparted
              ? TIME_CHART_VARIANTS.COMPLETE
              : hasArrived
              ? TIME_CHART_VARIANTS.IN_PROGRESS
              : TIME_CHART_VARIANTS.UPCOMING,
            mainIcon: hasDeparted
              ? TIME_CHART_ICONS.DEPARTURE
              : hasArrived
              ? TIME_CHART_ICONS.ARRIVAL
              : null,
          };

          if (!isNullOrUndefined(existingChartItem)) {
            existingChartItem.itemBlocks.push(timeChartBlockInfo);
            sortByDateField(existingChartItem.itemBlocks, "startTime");
          } else if (!isNullOrUndefined(effectiveStand)) {
            timeChartItemsToRender.push({
              uuid: effectiveStand.uuid,
              itemBlocks: [timeChartBlockInfo],
              name: effectiveStand.name,
            });
          } else {
            // Not assigned items occupy an entire row
            timeChartItemsToRenderNotAssigned.push({
              uuid: turnaroundInfo.uuid,
              itemBlocks: [timeChartBlockInfo],
              name: t("not_assigned"),
              isInactive: true,
              startTime: timeChartBlockInfo.startTime,
            });
          }
        }
      }
      setIsInitialized(true);
    }
    sortByField(timeChartItemsToRender, "name");
    // Not assigned items sorted by startTime
    sortByField(timeChartItemsToRenderNotAssigned, "startTime", ["name"]);
    return [[...timeChartItemsToRender, ...timeChartItemsToRenderNotAssigned]];
  }, [t, currentUser, turnaroundsSummarySearch, searchQuery, airportDetail]);

  useEffect(() => {
    dispatch({
      type: "setSearchQuery",
      value: {
        searchTerm: "",
        searchPlaceholder: SEARCH_QUERY_PLACEHOLDERS.SEARCH,
      },
    });
  }, [dispatch]);

  const showEmptyState = isEmptyList(timeChartItems);

  return (
    <div className="stands-overview">
      <div className="stands-overview-header">
        <div className="titles">
          <h3>{t("stand_control")}</h3>
        </div>
      </div>
      <div className="stands-overview-body">
        {showEmptyState && (
          <div>
            <div className="empty-state">
              {isInitialized && t("no_results")}
              {!isInitialized && <LoadingIndicator />}
            </div>
          </div>
        )}
        {!showEmptyState && (
          <TimeChart
            size={TIME_CHART_SIZE.LARGE}
            columnTitle={t("stand_non_cap")}
            timeChartItems={timeChartItems}
            airportTimezone={airportTimezone}
            timelineRanges={timelineRanges}
            searchTerm={searchQuery?.searchTerm}
            itemClickHandler={(timeChartInfo) => {
              setSelectedTimeChartInfo(timeChartInfo);
            }}
            itemBlockClickHandler={
              hasEditTurnProfile
                ? (timeChartBlockInfo) => {
                    setSelectedTurnaroundUuidForEdit(timeChartBlockInfo.uuid);
                  }
                : null
            }
          />
        )}
        {!isNullOrUndefined(selectedTurnaroundUuidForEdit) && (
          <TurnaroundEditModal
            selectedTurnaroundUuid={selectedTurnaroundUuidForEdit}
            onCloseHandler={() => {
              setSelectedTurnaroundUuidForEdit(null);
            }}
            onSaved={() => {
              handleReloadTurnarounds();
            }}
            timezone={airportTimezone}
          />
        )}
        {!isNullOrUndefined(selectedTimeChartInfo) && (
          <StandDetailModal
            timeChartInfo={selectedTimeChartInfo}
            airportTimezone={airportTimezone}
            onClose={() => {
              setSelectedTimeChartInfo(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
export default StandControl;
